import { Alert, Skeleton, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { GenerateVideoForm } from '../components/GenerateVideoForm';
import { ServiceUnavailable } from '../components/ServiceUnavailable';
import { VideoJobList } from '../components/VideoJobList';
import { useGenerateVideoConfig } from '../hooks/useGenerateVideoConfig';
import { useVideoJobs } from '../hooks/useVideoJobs';

type VideoHistoryProps = {
  jobs: ReturnType<typeof useVideoJobs>;
};

const VideoHistory: React.FC<VideoHistoryProps> = ({ jobs }) => {
  const { t } = useTranslation();

  return (
    <section className="min-w-0 space-y-3">
      <Typography variant="subtitle1" className="font-semibold text-gray-900 dark:text-slate-100">
        {t('services.generateVideo.history')}
      </Typography>
      <VideoJobList
        jobs={jobs.jobs}
        loading={jobs.loading}
        loadingMore={jobs.loadingMore}
        hasMore={jobs.hasMore}
        onLoadMore={() => void jobs.loadMore()}
        onCancel={jobs.cancel}
      />
    </section>
  );
};

/** /services/generate-video: form on the left, job history on the right (stacked on mobile). */
export const GenerateVideoPage: React.FC = () => {
  const { t } = useTranslation();
  const { config, loading, failed, refresh } = useGenerateVideoConfig();
  const jobs = useVideoJobs({
    minPollSeconds: config?.pollIntervalSeconds ?? 0,
    // Balance changes once a job settles.
    onJobFinished: () => void refresh(),
  });

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 p-4 md:p-6 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
        <Skeleton variant="rounded" height={520} />
        <Skeleton variant="rounded" height={520} />
      </div>
    );
  }

  if (failed || !config) {
    return (
      <div className="p-4 md:p-6">
        <Alert severity="error">{t('services.generateVideo.loadFailed')}</Alert>
      </div>
    );
  }

  const enabled = config.operations.filter((operation) => operation.enabled);
  if (enabled.length === 0) {
    const lockedByPackage =
      config.operations.length > 0 &&
      config.operations.every((operation) => operation.reason === 'not_in_package');

    if (lockedByPackage) {
      return (
        <div className="grid min-h-full grid-cols-1 gap-6 bg-slate-50 p-4 dark:bg-slate-950 md:p-6 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
          <ServiceUnavailable reason="not_in_package" />
          <VideoHistory jobs={jobs} />
        </div>
      );
    }

    return (
      <div className="p-4 md:p-6">
        <ServiceUnavailable reason={config.operations[0]?.reason ?? null} />
      </div>
    );
  }

  return (
    <div className="grid min-h-full grid-cols-1 gap-6 bg-slate-50 p-4 dark:bg-slate-950 md:p-6 lg:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
      <section className="h-fit min-w-0 rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 md:p-5 lg:sticky lg:top-4">
        <GenerateVideoForm
          config={config}
          onCreated={(job) => {
            jobs.addJob(job);
            void refresh();
          }}
        />
      </section>
      <VideoHistory jobs={jobs} />
    </div>
  );
};
